import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faClock, faNewspaper } from '@fortawesome/free-solid-svg-icons';
import LoadingCenter from '../util/LoadingCenter';
import { entryMinutes, fmtDuration } from './tsHelpers';

dayjs.extend(isoWeek);

interface Props {
  access?: any;
  baseUrl?: string;
}

const MobileDashboard: React.FC<Props> = () => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const navigate = useNavigate();
  const fmt = (min: number) => fmtDuration(min, t);

  const [user, setUser] = useState<any>(null);
  const [reports, setReports] = useState<any[]>([]);
  const [news, setNews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, n] = await Promise.allSettled([
        axios.get('user'),
        axios.get('time-reports'),
        axios.get('news'),
      ]);
      if (u.status === 'fulfilled') setUser(u.value.data);
      if (r.status === 'fulfilled') setReports(Array.isArray(r.value.data) ? r.value.data : []);
      if (n.status === 'fulfilled') setNews(Array.isArray(n.value.data) ? n.value.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (auth) load(); }, [auth, load]);

  const perWeek = user?.pattern?.hoursPerWeek ?? 0;

  // Текущая ISO-неделя
  const week = useMemo(() => {
    const mon = dayjs().startOf('isoWeek' as any);
    return Array.from({ length: 7 }, (_, i) => mon.add(i, 'day'));
  }, []);
  const weekKeys = useMemo(() => week.map((d) => d.format('DD.MM.YYYY')), [week]);

  const { weekMin, pending, dayTotals } = useMemo(() => {
    const rows = (reports || []).filter((r: any) => weekKeys.includes(moment(r.date).format('DD.MM.YYYY')));
    const totals: Record<string, number> = {};
    let total = 0;
    let pend = 0;
    rows.forEach((r: any) => {
      const min = entryMinutes(r);
      total += min;
      const key = moment(r.date).format('YYYY-MM-DD');
      totals[key] = (totals[key] || 0) + min;
      if (r.status === 'pending') pend += 1;
    });
    return { weekMin: total, pending: pend, dayTotals: totals };
  }, [reports, weekKeys]);

  const normMin = perWeek * 60;
  const progress = normMin > 0 ? Math.min(100, Math.round((weekMin / normMin) * 100)) : 0;
  const toil = user?.totalToils;

  if (loading) return <LoadingCenter />;

  const recentNews = [...news]
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
    .slice(0, 3);

  return (
    <div className="m-dash">
      {/* Приветствие */}
      <div className="m-hero" onClick={() => navigate('/timesheets')}>
        <h2>{t('Hi')}, {user?.first_name || ''} 👋</h2>
        {(user?.company?.title || user?.company?.unit) && (
          <p>{[user?.company?.title, user?.company?.unit].filter(Boolean).join(' · ')}</p>
        )}
        <div className="m-hero-row">
          <div className="m-hero-chip"><div className="v">{fmt(weekMin)}</div><div className="k">{t('ThisWeek')}</div></div>
          {toil !== undefined && toil !== null && (
            <div className="m-hero-chip"><div className="v">{toil}{t('hShort')}</div><div className="k">{t('TOILBalance')}</div></div>
          )}
        </div>
      </div>

      {/* Быстрые показатели */}
      <div className="m-stat-grid">
        <div className="m-stat">
          <span className="m-stat-ic" style={{ background: 'rgba(37,99,235,.15)' }}><FontAwesomeIcon icon={faLayerGroup} style={{ color: '#2563eb' }} /></span>
          <div className="m-stat-v">{fmt(weekMin)}</div>
          <div className="m-stat-k">{t('Worked')}</div>
        </div>
        <div className="m-stat">
          <span className="m-stat-ic" style={{ background: 'rgba(217,119,6,.15)' }}><FontAwesomeIcon icon={faClock} style={{ color: '#d97706' }} /></span>
          <div className="m-stat-v">{pending}</div>
          <div className="m-stat-k">{t('OnApproval')}</div>
        </div>
      </div>

      {/* Эта неделя */}
      <div className="m-dash-card" onClick={() => navigate('/timesheets')}>
        <div className="section-label" style={{ marginBottom: 10 }}>{t('ThisWeek')}</div>
        <div className="m-wk-top">
          <div className="m-wk-val">{fmt(weekMin)}{perWeek > 0 && <span> / {perWeek}{t('hShort')}</span>}</div>
          {perWeek > 0 && <div className="m-wk-pct">{progress}%</div>}
        </div>
        {perWeek > 0 && <div className="m-ts-bar"><i style={{ width: `${progress}%` }} /></div>}
        <div className="m-wk-mini">
          {week.map((d) => {
            const key = d.format('YYYY-MM-DD');
            const min = dayTotals[key] || 0;
            const isToday = d.isSame(dayjs(), 'day');
            return (
              <div key={key} className={`m-wk-cell${isToday ? ' today' : ''}${min ? ' has' : ''}`}>
                <span className="d">{d.format('dd')}</span>
                <span className="h">{min ? (min / 60).toFixed(min % 60 ? 1 : 0) : '—'}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Новости */}
      <div className="m-dash-card">
        <div className="section-label" style={{ marginBottom: 10 }}>{t('News')}</div>
        {recentNews.length === 0 ? (
          <div className="m-ts-noentries">{t('NoNews')}</div>
        ) : (
          <div className="flex flex-col gap-[12px]">
            {recentNews.map((item: any, i: number) => (
              <div key={item.id ?? i} className="m-news">
                <div className="m-news-th" style={{ background: item.emojiColor || undefined }}>
                  {item.emoji ? <span>{item.emoji}</span> : <FontAwesomeIcon icon={faNewspaper} style={{ color: '#fff' }} />}
                </div>
                <div className="m-news-ct">
                  <div className="m-news-tt">{item.title}</div>
                  {item.body && <div className="m-news-bd">{item.body}</div>}
                  {item.createdAt && (
                    <div className="m-news-mt">{new Date(item.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileDashboard;
